/**
 * Feedback Service
 * 
 * Handles submission of bug reports and feature requests to Firestore.
 */

import {
  collection,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../firebase'

// Root-level feedback collection (not project-scoped)
const FEEDBACK_COLLECTION = 'feedback'

/**
 * Submit feedback (bug report or feature request)
 * 
 * @param {object} feedback - The feedback data
 * @param {string} feedback.type - 'bug' | 'feature'
 * @param {string} feedback.title - Feedback title
 * @param {string} feedback.description - Detailed description
 * @param {string} feedback.userEmail - User's email
 * @param {string} feedback.userId - User's ID
 * @param {string} feedback.currentUrl - Current page URL
 * @param {string} feedback.userAgent - Browser user agent
 * @param {string} [feedback.projectId] - Current project ID (if applicable)
 * @param {string} [feedback.projectName] - Current project name (if applicable)
 * @returns {Promise<string>} - The created document ID
 */
export async function submitFeedback(feedback) {
  const {
    type,
    title,
    description,
    userEmail,
    userId,
    currentUrl,
    userAgent,
    projectId,
    projectName,
  } = feedback

  if (!type || !title) {
    throw new Error('Feedback type and title are required')
  }

  const docRef = await addDoc(collection(db, FEEDBACK_COLLECTION), {
    type,
    title,
    description: description || '',
    userEmail: userEmail || 'anonymous',
    userId: userId || null,
    currentUrl: currentUrl || '',
    userAgent: userAgent || '',
    projectId: projectId || null,
    projectName: projectName || null,
    status: 'new', // new | reviewed | in-progress | resolved | closed
    createdAt: serverTimestamp(),
  })

  return docRef.id
}
